import { usePhoneNumber } from '../../../../hooks';
import { useFormContext } from 'react-hook-form';
import { claimFields } from '../../../../config';
import {
  SummaryGrid,
  SummaryGroup,
  SummaryHeader,
  SummaryRow,
  SummarySection,
  SummaryText,
} from '../../../../components';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

import type { IClaim, IClaimInit } from '../../../../types';
import type { FC } from 'react';

interface IDamagerDetailsProps {
  claim?: IClaimInit | IClaim | null;
  setStep: (step: number) => void;
}

export const DamagerDetailsSummary: FC<IDamagerDetailsProps> = ({ claim, setStep }) => {
  const formContext = useFormContext();
  const { selectedLanguage } = useContext(LanguageSelectorContext);
  const localization = new Localization(selectedLanguage);

  const { formatPhoneNumber } = usePhoneNumber({});

  // DAMAGER KNOWN
  const hasOpinion: string = formContext.getValues(claimFields.opinionOnResponsibleParty)
    ? localization.translate('dropdownOptions.yes')
    : localization.translate('dropdownOptions.no');
  const billableParty: string =
    claim?.billablePartyTypeList?.find((b) => b.id === formContext.getValues(claimFields.responsibleForDamage))
      ?.name ?? localization.translate('dropdownOptions.na');

  // DAMAGER DETAILS
  const haveDamagerDetails = formContext.getValues(claimFields.haveDamagerContactDetails)
    ? localization.translate('dropdownOptions.yes')
    : localization.translate('dropdownOptions.no');
  const isDamagerKnown: string = formContext.getValues(claimFields.whoWasDamager)
    ? localization.translate('dropdownOptions.yes')
    : localization.translate('dropdownOptions.no');
  const damagerName: string =
    formContext.getValues(claimFields.damagerName)?.length !== 0
      ? formContext.getValues(claimFields.damagerName)
      : localization.translate('dropdownOptions.na');
  const damagerContactName: string =
    formContext.getValues(claimFields.damagerContactName)?.length !== 0
      ? formContext.getValues(claimFields.damagerContactName)
      : localization.translate('dropdownOptions.na');
  const phoneNumber: string = formContext.getValues(claimFields.damagerPhoneNumber) ?? '';
  const formattedPhoneNumber: string =
    phoneNumber.length > 0 ? formatPhoneNumber(phoneNumber) : localization.translate('dropdownOptions.na');
  const damagerEmailAddress: string = formContext.getValues(claimFields.damagerEmailAddress) ?? '';
  const damagerEmailAddressDisplay: string =
    damagerEmailAddress.length > 0 ? damagerEmailAddress : localization.translate('dropdownOptions.na');
  const howWasDamagerIdentified: string =
    formContext.getValues(claimFields.damagerIdentified)?.length !== 0
      ? formContext.getValues(claimFields.damagerIdentified)
      : localization.translate('dropdownOptions.na');
  const damagerStatement: string =
    formContext.getValues(claimFields.damagerStatement)?.length !== 0
      ? formContext.getValues(claimFields.damagerStatement)
      : localization.translate('dropdownOptions.na');

  // Return to damager details step
  const onStepClick = () => setStep(3);

  return (
    <div className={'flex flex-col'}>
      <SummaryHeader isLink onLink={onStepClick}>
        {localization.translate('damagerDetailsSummary.stepLink')}
      </SummaryHeader>

      <SummaryGroup>
        <SummarySection>
          <SummaryGrid>
            {/* DAMAGER OPINION */}
            <SummaryRow
              hideColon
              label={localization.translate('damagerDetailsSummary.opinionLabel')}
            >
              {hasOpinion}
            </SummaryRow>
            <SummaryRow
              hideColon
              label={localization.translate('damagerDetailsSummary.responsiblePartyLabel')}
            >
              {billableParty}
            </SummaryRow>

            {/* DAMAGER KNOWN */}
            <SummaryRow
              hideColon
              label={localization.translate('damagerDetailsSummary.isDamagerKnownLabel')}
            >
              {isDamagerKnown}
            </SummaryRow>
            <SummaryRow label={localization.translate('damagerDetailsSummary.damagerNameLabel')}>
              {damagerName}
            </SummaryRow>
            <SummaryText
              hideColon
              label={localization.translate('damagerDetailsSummary.howWasDamagerIdentifiedLabel')}
              value={howWasDamagerIdentified}
            />
          </SummaryGrid>
        </SummarySection>

        {/* CONTACT DETAILS */}
        <SummarySection>
          <SummaryGrid>
            <SummaryRow
              hideColon
              label={localization.translate('damagerDetailsSummary.haveDamagerDetailsLabel')}
            >
              {haveDamagerDetails}
            </SummaryRow>
            <SummaryRow label={localization.translate('damagerDetailsSummary.damagerContactNameLabel')}>
              {damagerContactName}
            </SummaryRow>
            <SummaryRow label={localization.translate('damagerDetailsSummary.damagerPhoneNumberLabel')}>
              {formattedPhoneNumber}
            </SummaryRow>
            <SummaryRow label={localization.translate('damagerDetailsSummary.damagerEmailAddressLabel')}>
              {damagerEmailAddressDisplay}
            </SummaryRow>
            <SummaryText
              label={localization.translate('damagerDetailsSummary.damagerStatementLabel')}
              value={damagerStatement}
            />
          </SummaryGrid>
        </SummarySection>
      </SummaryGroup>
    </div>
  );
};